import React from 'react';
import styled from 'styled-components';
import { StyledLabel } from '../../../shared';
import RiskPercentageDropDown from './components/RiskPercentageDropDown';
import type { DropMenuItem } from 'venn-ui-kit';
import { DropMenu } from 'venn-ui-kit';
import { range } from 'lodash';
import { DEFAULT_SCALE, DEFAULT_YEARS } from '../../../../logic/studioUtils';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  blockConfidenceLevels,
  blockConfidenceLevelsVisibility,
  blockForecastWindow,
  blockInfoGraphicType,
  blockLogarithmicScale,
} from 'venn-state';
import { PanelSection } from '../../../PanelSection';

export interface GrowthSimulationSectionProps {
  selected: string;
}

const getYearOptions = (n: number) => {
  return range(n).map((v) => ({ label: String(v + 1), value: v + 1 })); // exclude 0
};

const getRiskOptions = () => {
  const riskOptions: DropMenuItem<number>[] = [];
  for (let i = 0; i <= 100; i += 5) {
    if (i === 0) {
      riskOptions.push({ label: '1%', value: 0.01 });
    } else if (i === 100) {
      riskOptions.push({ label: '99%', value: 0.99 });
    } else {
      riskOptions.push({ label: `${String(i)}%`, value: i / 100 });
    }
  }
  return riskOptions;
};

const scaleOptions: DropMenuItem<boolean>[] = [
  { label: 'Logarithmic', value: true },
  { label: 'Linear', value: false },
];

const GrowthSimulationSection = ({ selected }: GrowthSimulationSectionProps) => {
  const infographicType = useRecoilValue(blockInfoGraphicType(selected));
  const [confidenceLevels, setConfidenceLevels] = useRecoilState(blockConfidenceLevels(selected));
  const [forecastWindow, setForecastWindow] = useRecoilState(blockForecastWindow(selected));
  const [confidenceLevelsVisibility, setConfidenceLevelsVisibility] = useRecoilState(
    blockConfidenceLevelsVisibility(selected),
  );
  const [logarithmicScale, setLogarithmicScale] = useRecoilState(blockLogarithmicScale(selected));

  const yearOptions = getYearOptions(30);
  const riskOptions = getRiskOptions();

  const isBoxChart = infographicType === 'BOX_CHART';

  return (
    <PanelSection header="Simulation Settings" className="qa-growth-simulation-settings">
      <Container>
        <div>
          <StyledLabel>Years Projected:</StyledLabel>
          <DropMenu
            items={yearOptions}
            selected={forecastWindow ?? DEFAULT_YEARS}
            className="qa-years-projected-dropdown"
            width={110}
            onChange={(item) => setForecastWindow(item.value)}
          />
        </div>
        <div>
          <StyledLabel>Scale:</StyledLabel>
          <DropMenu
            items={scaleOptions}
            selected={logarithmicScale ?? DEFAULT_SCALE}
            className="qa-logarithmic-scale"
            width={110}
            onChange={(item) => setLogarithmicScale(item.value)}
          />
        </div>
      </Container>
      <PaddingWrapper>
        <StyledLabel>Risk Percentage:</StyledLabel>
        <Container>
          {confidenceLevels.map((risk, i) => (
            <RiskPercentageDropDown
              selected={risk}
              onSelectRiskPercentage={setConfidenceLevels}
              onSelectVisibility={setConfidenceLevelsVisibility}
              items={riskOptions}
              index={i}
              visible={confidenceLevelsVisibility[i]}
              visibility={confidenceLevelsVisibility}
              riskPercentages={confidenceLevels}
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              disabled={isBoxChart}
            />
          ))}
        </Container>
      </PaddingWrapper>
    </PanelSection>
  );
};

export default GrowthSimulationSection;

const PaddingWrapper = styled.div`
  padding-top: 15px;
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
